import type { Glyph, Orbit, Tone } from '../../flair/engine';
import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part } from '../body';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import { type Ellipse, fromSegment, type HeadPicture, inEllipse, mask, type SetFlair } from './mythic';

/**
 * The Disco set (ADR-0160 amended, mythic): the dance floor's king. A mirror ball for a head behind star shades, a
 * sequinned jumpsuit with a wide white collar, and the Boogie Blaster.
 *
 * The head is a mirror ball (a mask over the whole head): a sphere of flat square facets in rows of latitude, each one
 * lit as a little mirror on its own (the light from the top-left, a facet's brightness jittered as mirrors catch the
 * room unevenly), a few facets catching the floor's coloured lights (pink, gold, lime and the team's), dark grout between
 * them; a chrome cap and a hanging ring on its crown; across its face star-shaped shades in the team's colour in gold
 * rims. Its flair is the ball catching the light: four-pointed glints flaring on its facets one after another, small
 * sparkles coming and going over it. The suit is a jumpsuit in the team's colour sewn with sequins, a wide white dagger
 * collar open on a V of skin and a gold medallion, puffed sequinned shoulders, a wide gold belt with a mirror buckle, white
 * platform boots. Its flair: sequins glinting, the ball's spots of coloured light wheeling round its feet on the floor,
 * lit dance-floor tiles where it steps on the run.
 *
 * Rubric (`assetc mythic disco` at 5× and 1:1 on grass, lane and night, both teams, idle, run and dead, beside Inferno
 * and Glitch): ball S5 R5 P4 L4 C4 A4 F5, jumpsuit S4 R4 P4 L4 C4 F4. Still weak: the facets are 4-6 px, a fine grey
 * texture at 1:1 that the glints and the shades carry; the jumpsuit shows little past the gun (collar, shoulders, belt).
 */

// --- The mirror ball -------------------------------------------------------------------------------------------------

/** From the head's middle (u forward, v down): the ball, and its facets' angular size (radians a row, a column). */
const BALL = { u: 1.5, v: -3, rx: 21.5, ry: 20.5 } as const;
const STEP = Math.PI / 13;
/** The cap and the ring it hangs from, on its crown. */
const CAP: Ellipse = [1.5, -23.3, 4.2, 2];
const RING = [1.5, -27.2, 2.4] as const;
/** The star shades: [middle u, middle v, outer radius, turn]; a gold bridge between them. */
const SHADES = [
  [-3.5, 0.5, 6.6, -0.12],
  [11.5, 0.5, 5.9, 0.15],
] as const;

/** A facet's own little hash: 0..1. */
const hash = (a: number, b: number): number => {
  let h = Math.imul(a * 73856093 + 19349663, 0x9e3779b1) ^ Math.imul(b + 83492791, 0x85ebca6b);
  h ^= h >>> 15;
  h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 12;
  return (h >>> 0) / 4294967296;
};

/** Which facet (u, v) is on: its row and column, or null off the ball. */
function facet(u: number, v: number): readonly [number, number] | null {
  const [nx, ny] = [(u - BALL.u) / BALL.rx, (v - BALL.v) / BALL.ry];
  const nn = nx * nx + ny * ny;
  if (nn > 1) return null;
  const nz = Math.sqrt(1 - nn);
  const lat = Math.asin(Math.max(-1, Math.min(1, ny)));
  const row = Math.floor(lat / STEP);
  // Columns as wide on the ball at every row: fewer of them toward the poles.
  const across = Math.cos((row + 0.5) * STEP);
  const lon = Math.atan2(nx, nz);
  return [row, Math.floor((lon * across) / STEP + 0.37 * (row & 1))];
}

/** How far a five-pointed star turned by `a` reaches at (dx, dy): under 1 inside it. */
function inStar(dx: number, dy: number, r: number, a: number): number {
  const t = Math.atan2(dy, dx) - a + Math.PI / 2;
  const k = ((((t / (Math.PI * 2)) * 5) % 1) + 1) % 1;
  const reach = r * (0.5 + 0.5 * Math.abs(k * 2 - 1) ** 1.3);
  return Math.hypot(dx, dy) / reach;
}

/** What the floor's lights paint a facet that catches one: pink, gold, lime or the team's. */
const CAUGHT: readonly (readonly [PaletteKey, PaletteKey])[] = [
  ['i', 'p'],
  ['y', 'Y'],
  ['l', 'g'],
  ['c', 'b'],
];

/**
 * The stand-up collar behind the ball's lower half: white, turned up into a point either side (the 70s showman's), piped
 * in the team's colour along its rim, lit on its near (left) wing, shaded on its far one.
 */
function collar(u: number, v: number): PaletteKey | null {
  const du = Math.abs(u - BALL.u);
  if (du > 23.5) return null;
  const top = 15 - (du / 23.5) ** 2.2 * 22;
  const bottom = 18.5 - du * 0.12;
  if (v < top || v > bottom) return null;
  const near = u < BALL.u;
  if (v < top + 1.6) return near ? 'c' : 'b';
  if (v < top + 2.6) return near ? 'b' : 'B';
  if (v > bottom - 1) return near ? '4' : '3';
  // A fold down its middle, the wing lit on its outer side.
  const t = (v - top) / (bottom - top);
  if (Math.abs(t - 0.55) < 0.06) return near ? '5' : '4';
  return near ? (t < 0.55 ? 'w' : '5') : t < 0.55 ? '5' : '4';
}

const ball: HeadPicture = (u, v, lx, ly) => {
  // The hanging ring and the chrome cap on the crown.
  const ring = Math.hypot(u - RING[0], v - RING[1]);
  if (ring < RING[2] && ring > RING[2] - 1.2) return u - RING[0] + v - RING[1] < 0 ? '5' : '3';
  if (inEllipse(u, v, CAP) <= 1) return (u - CAP[0]) * lx + (v - CAP[1]) * ly * 2 > 0.5 ? 'w' : v > -21.5 ? '3' : '4';
  // The shades: stars in the team's colour in gold rims, a bridge between them, a glint on each.
  for (const [cu, cv, r, a] of SHADES) {
    const s = inStar(u - cu, v - cv, r, a);
    if (s >= 1) continue;
    if (s > 0.74) return (u - cu) * lx + (v - cv) * ly > 0 ? 'y' : 'Y';
    if (Math.hypot(u - cu + 1.6, v - cv + 1.6) < 1.1) return 'w';
    const l = ((u - cu) * lx + (v - cv) * ly) / r;
    return l > 0.25 ? 'c' : l > -0.3 ? 'b' : 'B';
  }
  if (fromSegment(u, v, 2, -0.6, 6, -0.6) < 0.8) return 'Y';
  const f = facet(u, v);
  if (!f) return collar(u, v);
  const [nx, ny] = [(u - BALL.u) / BALL.rx, (v - BALL.v) / BALL.ry];
  // Its rim: a dark edge where the ball turns away, so it reads as a sphere.
  const rr = nx * nx + ny * ny;
  // Grout: dark lines along each facet's top and near edge (its neighbour above or behind is another facet).
  const up = facet(u, v - 1);
  const back = facet(u - 1, v);
  const grout = (up && (up[0] !== f[0] || up[1] !== f[1])) || (back && (back[0] !== f[0] || back[1] !== f[1]));
  // The facet's own mirror: lit by its middle's normal, jittered.
  const lat = (f[0] + 0.5) * STEP;
  const across = Math.cos(lat);
  const lon = ((f[1] + 0.5 - 0.37 * (f[0] & 1)) * STEP) / across;
  const n = [Math.cos(lat) * Math.sin(lon), Math.sin(lat), Math.cos(lat) * Math.cos(lon)] as const;
  const light = n[0] * lx * 0.72 + n[1] * ly * 0.72 + n[2] * 0.62;
  const jitter = hash(f[0] + 40, f[1] + 40);
  if (grout) return light > 0.7 && rr < 0.8 ? '2' : '1';
  // A few facets catch the floor's lights, the brighter on the lit side.
  if (jitter > 0.9 && rr < 0.86) {
    const [hi, lo] = CAUGHT[Math.floor(hash(f[1] + 7, f[0] + 3) * CAUGHT.length)] as readonly [PaletteKey, PaletteKey];
    return light > 0.5 ? hi : lo;
  }
  const l = light + (jitter - 0.5) * 0.55 - (rr > 0.86 ? 0.35 : 0);
  return l > 0.93 ? 'w' : l > 0.72 ? '5' : l > 0.48 ? '4' : l > 0.22 ? '3' : l > -0.05 ? '2' : '1';
};

// --- The sequinned jumpsuit ------------------------------------------------------------------------------------------

/** A sequin's glint at (x, y) of a field: a scatter of them, two in three dim, one bright. */
const sequin = (x: number, y: number): 0 | 1 | 2 => {
  if ((x * 3 + y * 5) % 7 !== 0 || y % 2 !== 0) return 0;
  return (x + y * 3) % 3 === 0 ? 2 : 1;
};

/**
 * The jumpsuit standing (the frame's x 5-56, y 39-57): the team's colour sewn with sequins, a wide white dagger collar
 * open on a V of skin with a gold medallion on it, puffed sequinned shoulders, a wide gold belt with a mirror buckle.
 */
function jumpsuit(): Part {
  const [x0, y0] = [5, 39];
  const c = new PixelCanvas(52, 19);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  const cloth = (x: number, y: number, lit: boolean, shade: boolean): PaletteKey => {
    const s = sequin(x, y);
    if (s === 2) return 'w';
    if (s === 1) return shade ? 'b' : 'c';
    return lit ? 'c' : shade ? 'B' : 'b';
  };
  for (let y = 44; y <= 52; y++) for (let x = 16; x <= 43; x++) at(x, y, cloth(x, y, x <= 18 || y === 44, x >= 40));
  // Puffed shoulders, sequinned, lit from the top-left.
  for (const [cx, rx] of [
    [12.5, 7],
    [47, 6.2],
  ] as const) {
    const e: Ellipse = [cx, 47.5, rx, 5.4];
    for (let y = 41; y <= 54; y++)
      for (let x = 5; x <= 56; x++) {
        const r = inEllipse(x + 0.5, y + 0.5, e);
        if (r > 1) continue;
        const l = -(x + 0.5 - cx) / rx - (y + 0.5 - 47.5) / 5.4;
        at(x, y, cloth(x, y, l > 0.6, l < -0.4 || (r > 0.8 && l < 0)));
      }
  }
  // The V of skin down to the chest, a gold chain round it and the medallion.
  for (let y = 42; y <= 49; y++) {
    const half = Math.max(0, Math.round((49 - y) * 0.75));
    for (let x = 30 - half; x <= 30 + half; x++) at(x, y, x === 30 + half && half > 0 ? 'S' : 's');
  }
  for (const [x, y] of [
    [26, 43],
    [27, 45],
    [28, 47],
    [34, 43],
    [33, 45],
    [32, 47],
  ] as const)
    at(x, y, 'Y');
  c.stamp(
    `
    .yY.
    yyYU
    YYUU
    .UU.
  `,
    29 - x0,
    48 - y0,
  );
  // The wide dagger collar: two white lapels spread over the shoulders, a point each.
  for (const side of [-1, 1] as const)
    for (let k = 0; k <= 9; k++) {
      const y = 42 + Math.floor(k * 0.75);
      const xin = 30 + side * (4 + Math.floor(k * 0.35));
      const xout = 30 + side * (7 + k);
      for (let x = Math.min(xin, xout); x <= Math.max(xin, xout); x++) {
        const edge = x === xout || y === 42 + Math.floor(9 * 0.75);
        at(x, y, edge ? (side < 0 ? '5' : '4') : side < 0 ? 'w' : '5');
      }
      // Its foot shaded.
      at(xout, y + 1, side < 0 ? '4' : '3');
    }
  // The wide gold belt, a mirror buckle.
  for (let x = 16; x <= 43; x++) {
    at(x, 53, x <= 18 ? 'y' : 'Y');
    at(x, 54, 'Y');
    at(x, 55, x >= 40 ? 'u' : 'U');
  }
  c.stamp(
    `
    w55w4
    5w454
    45w43
  `,
    28 - x0,
    53 - y0,
  );
  return [c, x0, y0];
}

/** The jumpsuit lying (right of the legs, under the head): the gold belt at the waist (left), sequins, the collar. */
const DEAD_SUIT = grid(`
  ....bbcc
  ...bcbcw
  yYbcbbw5
  YUbbwbw5
  YUbcbbsw
  YUbbbssw
  ywbbYsw5
  Y5bcYYs5
  YUbbbssw
  YUcbbbsw
  YUbbwbw5
  YUbbbbw5
  ywbcbbw5
  YUbbbbcw
  YUbwbbc.
  YUbbbbcb
  YUBBBBBB
  ....BBBB
`);

/** White platform boots under the team's trousers (the suit's legs). */
const JUMPSUIT = (): Outfit => ({ torso: jumpsuit(), dead: [DEAD_SUIT, 24, 51], legs: { n: '5', N: '4', m: 'w' } });

// --- The Boogie Blaster's finish on every other gun ------------------------------------------------------------------

/**
 * The Boogie finish (the default gun is its own, `pollinator_disco`): chrome where the steel was, purple and pink where
 * the wood was, bright gold where the brass was.
 */
const BOOGIE = {
  '3': '4',
  '4': '5',
  '5': 'w',
  N: 'k',
  n: 'P',
  m: 'p',
  t: 'i',
  e: 'w',
  U: 'Y',
  Y: 'y',
  y: 'w',
} as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

const WHITE = 0xffffff;
const SILVER = 0xc0cbdc;
const YELLOW = 0xfee761;
const PINK = 0xf6757a;
const MAGENTA = 0xb55088;
const LIME = 0x63c74d;
const GOLD = 0xfeae34;

/** A four-pointed glint flaring on a facet: big, then small (a frame each). */
const GLINT = [
  `
  ....w....
  ....w....
  ...s5s...
  .ss5w5ss.
  ww5www5ww
  .ss5w5ss.
  ...s5s...
  ....w....
  ....w....
`,
  `
  .........
  .........
  ....w....
  ...s5s...
  ..w5w5w..
  ...s5s...
  ....w....
  .........
  .........
`,
].map((g) =>
  g
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length)
    .join('\n'),
);

/** Glints flaring on the ball one after another, somewhere new on it each time. */
const glint = (hold: readonly [number, number], frames: readonly string[], shown: number): Glyph => ({
  kind: 'glyph',
  at: { on: 'head', x: 27, y: 21 },
  frames,
  tones: { w: WHITE, '5': SILVER, s: YELLOW },
  hold,
  order: 'cycle',
  shown,
  box: [30, 28],
  glow: [7, 0.55],
  add: true,
  layer: 'front',
});

/** A spot of the ball's light wheeling round the feet on the floor, in `tone`; each its own pace, so they drift apart. */
const spot = (tone: Tone, period: number, ry: number): Orbit => ({
  kind: 'orbit',
  at: { on: 'feet', x: 30, y: 67 },
  rx: 30,
  ry,
  n: 1,
  period,
  size: 3,
  tones: [tone, tone],
  tail: 0.06,
  glow: 4,
});

/** A lit tile left where it stepped on the run, in `tone`. */
const step = (tone: number) =>
  ({
    kind: 'emit',
    at: { on: 'feet', x: 30, y: 67 },
    box: [14, 4],
    rate: 0,
    run: 0.02,
    v: [0, 0, 0, 0],
    life: [0.35, 0.55],
    size: [4, 4],
    tall: 0.75,
    tones: [tone],
    alpha: [0.9, 0],
    add: true,
    glow: 1.6,
    layer: 'back',
  }) as const;

const FLAIR: SetFlair = {
  head: [
    glint([0.32, 0.5], GLINT, 0.55),
    glint([0.21, 0.37], [GLINT[1] as string], 0.5),
    // Small sparkles coming and going all over it.
    {
      kind: 'twinkle',
      at: { on: 'head', x: 31, y: 26 },
      box: [34, 34],
      round: true,
      n: 4,
      period: [0.35, 0.7],
      tones: [WHITE, SILVER],
      add: true,
      layer: 'front',
    },
  ],
  outfit: [
    // Its sequins glinting.
    {
      kind: 'twinkle',
      at: { on: 'torso', x: 30, y: 48 },
      box: [36, 12],
      n: 3,
      period: [0.3, 0.55],
      tones: [WHITE, SILVER],
      add: true,
      layer: 'front',
    },
    // The ball's spots of light wheeling round its feet.
    spot(PINK, 3.1, 8),
    spot(YELLOW, -3.7, 7),
    spot(LIME, 4.3, 9),
    spot('teamLight', -2.7, 8),
    // Dance-floor tiles lit where it steps.
    step(PINK),
    step(YELLOW),
    step(LIME),
    step(MAGENTA),
  ],
  gun: [
    // The little mirror ball on its handle glinting, and its speaker's beat.
    {
      kind: 'twinkle',
      at: { on: 'gun', x: 34, y: 8 },
      box: [12, 10],
      round: true,
      n: 2,
      period: [0.3, 0.5],
      tones: [WHITE, YELLOW],
      add: true,
      layer: 'gun',
    },
    {
      kind: 'pulse',
      at: { on: 'gun', x: 34, y: 8 },
      r: [3, 6],
      alpha: [0.25, 0.5],
      period: 0.5,
      tone: GOLD,
      layer: 'gun',
    },
    {
      kind: 'pulse',
      at: { on: 'gun', x: 60, y: 20 },
      r: [2, 4],
      alpha: [0.2, 0.45],
      period: 0.5,
      tone: PINK,
      layer: 'gun',
    },
  ],
};

/** The Disco set (ADR-0160 amended). */
export const DISCO = drawnLater({ head: () => mask(ball), outfit: JUMPSUIT, gun: BOOGIE, flair: FLAIR });
